'use client';

import {
  AUDIO_OPTIONS,
  CAMERA_OPTIONS,
  DIALOGUE_OPTIONS,
  GRAPHICS_OPTIONS,
  LIGHTING_OPTIONS,
  MOTION_OPTIONS,
  PRESET_PROMPTS,
} from '@/hooks/generator-constants';
import {
  Activity,
  Camera,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Palette,
  Sun,
  Type,
  Volume2,
} from 'lucide-react';
import React from 'react';

interface GeneratorEnhanceProps {
  activeDropdown: string | null;
  openSection: string | null;
  toggleSection: (section: string) => void;
  selectOption: (type: string, val: string, label: string) => void;
  setOpenSection: (section: string | null) => void;
}

export function GeneratorEnhance({
  activeDropdown,
  openSection,
  toggleSection,
  selectOption,
  setOpenSection,
}: GeneratorEnhanceProps) {
  if (activeDropdown !== 'settings') return null;

  return (
    <div className="select-options active sora-settings-dropdown !px-0">
      {/* Style */}
      <div className="border-white/10 border-b">
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between px-5 py-4 transition-colors hover:bg-white/5 active:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            toggleSection('style');
          }}
        >
          <span className="flex items-center gap-1.5 font-bold text-[13px] text-gray-400 uppercase">
            <Palette className="h-3 w-3" /> Style
          </span>
          <div className="flex items-center text-gray-400">
            {openSection === 'style' ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </div>
        </button>
        {openSection === 'style' && (
          <div className="grid grid-cols-1 gap-1 bg-black/20 p-2">
            <div>
              {Object.entries(PRESET_PROMPTS).map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  className="option !text-[13px] !py-3 w-full px-4 text-left capitalize"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenSection(null);
                    selectOption('presets', val, label);
                  }}
                >
                  <span className="capitalize">{val.replace(/-/g, ' ')}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Camera */}
      <div className="border-white/10 border-b">
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between px-5 py-4 transition-colors hover:bg-white/5 active:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            toggleSection('camera');
          }}
        >
          <span className="flex items-center gap-1.5 font-bold text-[13px] text-gray-400 uppercase">
            <Camera className="h-3 w-3" /> Camera
          </span>
          <div className="flex items-center text-gray-400">
            {openSection === 'camera' ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </div>
        </button>
        {openSection === 'camera' && (
          <div className="grid grid-cols-1 gap-1 bg-black/20 p-2">
            {Object.entries(CAMERA_OPTIONS).map(([val, label]) => (
              <button
                key={val}
                type="button"
                className="option !text-[13px] !py-3 w-full px-4 text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenSection(null);
                  selectOption('camera', val, label);
                }}
              >
                <span className="capitalize">{val.replace(/-/g, ' ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lighting */}
      <div className="border-white/10 border-b">
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between px-5 py-4 transition-colors hover:bg-white/5 active:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            toggleSection('lighting');
          }}
        >
          <span className="flex items-center gap-1.5 font-bold text-[13px] text-gray-400 uppercase">
            <Sun className="h-3 w-3" /> Lighting
          </span>
          <div className="flex items-center text-gray-400">
            {openSection === 'lighting' ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </div>
        </button>
        {openSection === 'lighting' && (
          <div className="grid grid-cols-1 gap-1 bg-black/20 p-2">
            {Object.entries(LIGHTING_OPTIONS).map(([val, label]) => (
              <button
                key={val}
                type="button"
                className="option !text-[13px] !py-3 w-full px-4 text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenSection(null);
                  selectOption('lighting', val, label);
                }}
              >
                <span className="capitalize">{val.replace(/-/g, ' ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Motion */}
      <div className="border-white/10 border-b">
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between px-5 py-4 transition-colors hover:bg-white/5 active:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            toggleSection('motion');
          }}
        >
          <span className="flex items-center gap-1.5 font-bold text-[13px] text-gray-400 uppercase">
            <Activity className="h-3 w-3" /> Motion
          </span>
          <div className="flex items-center text-gray-400">
            {openSection === 'motion' ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </div>
        </button>
        {openSection === 'motion' && (
          <div className="grid grid-cols-1 gap-1 bg-black/20 p-2">
            {Object.entries(MOTION_OPTIONS).map(([val, label]) => (
              <button
                key={val}
                type="button"
                className="option !text-[13px] !py-3 w-full px-4 text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenSection(null);
                  selectOption('motion', val, label);
                }}
              >
                <span className="capitalize">{val.replace(/-/g, ' ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Graphics */}
      <div className="border-white/10 border-b">
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between px-5 py-4 transition-colors hover:bg-white/5 active:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            toggleSection('graphics');
          }}
        >
          <span className="flex items-center gap-1.5 font-bold text-[13px] text-gray-400 uppercase">
            <Type className="h-3 w-3" /> Graphics
          </span>
          <div className="flex items-center text-gray-400">
            {openSection === 'graphics' ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </div>
        </button>
        {openSection === 'graphics' && (
          <div className="grid grid-cols-1 gap-1 bg-black/20 p-2">
            {Object.entries(GRAPHICS_OPTIONS).map(([val, label]) => (
              <button
                key={val}
                type="button"
                className="option !text-[13px] !py-3 w-full px-4 text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenSection(null);
                  selectOption('graphics', val, label);
                }}
              >
                <span className="capitalize">{val.replace(/-/g, ' ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Audio */}
      <div className="border-white/10 border-b">
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between px-5 py-4 transition-colors hover:bg-white/5 active:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            toggleSection('audio');
          }}
        >
          <span className="flex items-center gap-1.5 font-bold text-[13px] text-gray-400 uppercase">
            <Volume2 className="h-3 w-3" /> Audio
          </span>
          <div className="flex items-center text-gray-400">
            {openSection === 'audio' ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </div>
        </button>
        {openSection === 'audio' && (
          <div className="grid grid-cols-1 gap-1 bg-black/20 p-2">
            {Object.entries(AUDIO_OPTIONS).map(([val, label]) => (
              <button
                key={val}
                type="button"
                className="option !text-[13px] !py-3 w-full px-4 text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenSection(null);
                  selectOption('audio', val, label);
                }}
              >
                <span className="capitalize">{val.replace(/-/g, ' ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Dialogue */}
      <div>
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between px-5 py-4 transition-colors hover:bg-white/5 active:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            toggleSection('dialogue');
          }}
        >
          <span className="flex items-center gap-1.5 font-bold text-[13px] text-gray-400 uppercase">
            <MessageSquare className="h-3 w-3" /> Dialogue
          </span>
          <div className="flex items-center text-gray-400">
            {openSection === 'dialogue' ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </div>
        </button>
        {openSection === 'dialogue' && (
          <div className="grid grid-cols-1 gap-1 bg-black/20 p-2">
            {Object.entries(DIALOGUE_OPTIONS).map(([val, label]) => (
              <button
                key={val}
                type="button"
                className="option !text-[13px] !py-3 w-full px-4 text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenSection(null);
                  selectOption('dialogue', val, label);
                }}
              >
                <span className="capitalize">{val.replace(/-/g, ' ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
